import { useQuery, useMutation } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { vaultApi } from "@/api/vault";
import { Lock, CheckCircle, XCircle, Key } from "lucide-react";

export function VaultSettings() {
  const { data: config } = useQuery({
    queryKey: ["vault", "config"],
    queryFn: vaultApi.getConfig,
  });

  const testMutation = useMutation({
    mutationFn: vaultApi.test,
  });

  const { data: secretsData } = useQuery({
    queryKey: ["vault", "secrets"],
    queryFn: vaultApi.listSecrets,
    enabled: !!config?.configured,
  });

  const secrets = secretsData?.secrets ?? [];

  return (
    <CenterPanel title="Vault Settings">
      {/* Connection */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Lock size={18} />
            Key Vault Connection
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-3">
            <div className="flex items-center gap-1.5">
              {config?.configured ? (
                <CheckCircle size={14} className="text-green-500" />
              ) : (
                <XCircle size={14} className="text-text-muted" />
              )}
              <span className="text-sm">
                {config?.configured ? "Configured" : "Not configured"}
              </span>
            </div>
            {config?.vaultUrl && (
              <span className="text-xs text-text-muted font-mono">{config.vaultUrl}</span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => testMutation.mutate()}
              disabled={!config?.configured || testMutation.isPending}
            >
              Test Connection
            </Button>
            {testMutation.data && (
              <Badge variant={testMutation.data.success ? "success" : "danger"}>
                {testMutation.data.success
                  ? `Connected (${testMutation.data.count} secrets)`
                  : testMutation.data.error}
              </Badge>
            )}
          </div>

          {!config?.configured && (
            <p className="mt-3 text-xs text-text-muted">
              Set AZURE_KEYVAULT_URL environment variable to configure.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Secrets list */}
      {config?.configured && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Key size={18} />
              Secrets ({secrets.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {secrets.length === 0 ? (
              <p className="text-sm text-text-muted">No secrets found in vault.</p>
            ) : (
              <div className="flex flex-col gap-1">
                {secrets.map((name) => (
                  <div
                    key={name}
                    className="flex items-center justify-between rounded px-2 py-1.5 hover:bg-surface-hover"
                  >
                    <span className="text-sm font-mono text-text-primary">{name}</span>
                    <span className="text-xs text-text-muted">••••</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </CenterPanel>
  );
}
